import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';
import { Country } from '../typings';
import { getCountryFromSymbol } from './countries-mapper';

const phoneUtil: PhoneNumberUtil = PhoneNumberUtil.getInstance();

const validateAndTruncatePhoneNumber = ({
  phone,
  countryCode,
  countrySymbol,
}: {
  phone: string;
  countryCode: string;
  countrySymbol: string;
}) => {
  try {
    const parsedNumber = phoneUtil.parse(phone, countryCode);
    const number = phoneUtil.format(parsedNumber, PhoneNumberFormat.E164);
    return number;
  } catch (error) {
    throw new Error('INVALID_PHONE');
  }
};

export default validateAndTruncatePhoneNumber;

const normalizeBrazilNumber = (number: string) => {
  // Normalize numbers starting with +55
  const intlMatch = number.match(/^\+55(\d{2})(\d{4})(\d{4})$/);
  if (intlMatch) {
    return `55${intlMatch[1]}9${intlMatch[2]}${intlMatch[3]}`;
  }

  // Normalize numbers starting with 55 (no +)
  const noPlusIntlMatch = number.match(/^55(\d{2})(\d{4})(\d{4})$/);
  if (noPlusIntlMatch) {
    return `55${noPlusIntlMatch[1]}9${noPlusIntlMatch[2]}${noPlusIntlMatch[3]}`;
  }

  // Handle already correct 11-digit local format (e.g., 11991234567)
  const local11Match = number.match(/^(\d{2})(9\d{4})(\d{4})$/);
  if (local11Match) {
    return `55${local11Match[1]}${local11Match[2]}${local11Match[3]}`;
  }

  // Handle 10-digit local format missing '9' (e.g., 1191234567)
  const local10Match = number.match(/^(\d{2})(\d{4})(\d{4})$/);
  if (local10Match) {
    return `55${local10Match[1]}9${local10Match[2]}${local10Match[3]}`;
  }

  return number;
};
export const validatePhoneNumber = ({
  phone,
  countryCode,
  setCountry,
}: {
  phone: string;
  countryCode: string;
  setCountry?: (value: Country) => void;
}) => {
  try {
    // Normalize Brazilian numbers from local or intl format
    const normalizedPhone = countryCode === 'BR' ? normalizeBrazilNumber(phone) : phone;

    const parsedNumber = phoneUtil.parseAndKeepRawInput(normalizedPhone, countryCode);
    const countrySymbol = parsedNumber.getCountryCode();
    const isValidNumber = phoneUtil.isValidNumber(parsedNumber);
    const country = getCountryFromSymbol(`+${countrySymbol}`, countryCode);
    if (country && typeof setCountry === 'function') {
      setCountry(country);
    }
    return isValidNumber;
  } catch (error) {
    return false;
  }
};

export const parsePhoneNumber = (phone: string) => {
  try {
    const number = phoneUtil.parseAndKeepRawInput(phone);
    return {
      countryCode: number.getCountryCode(),
      phone: number.getNationalNumber(),
    };
  } catch (error) {
    return {
      countryCode: undefined,
      phone: undefined,
    };
  }
};
